import { type ScheduleType, type ShareableState } from "@prisma/client";
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@workspace/ui/components";
import { InputTags } from "@workspace/ui/components/input-tags";
import { InfoIcon } from "lucide-react";
import { useEffect, useState } from "react";
import invariant from "tiny-invariant";
import { api } from "~/utils/api";
import { CODEWORDS_LIBRARY_USER_ID } from "~/utils/consts";
import { TooltipWrapper } from "../TooltipWrapper";

interface DevPanelWorkflowDataProps {
  serviceId: string;
}

export const DevPanelWorkflowData = ({
  serviceId,
}: DevPanelWorkflowDataProps) => {
  const [service] = api.service.getService.useSuspenseQuery({ id: serviceId });
  const utils = api.useUtils();
  const editServiceMutation = api.service.editService.useMutation({
    onSuccess: async () => {
      await utils.service.getService.invalidate({ id: serviceId });
      setHasChanges(false);
    },
  });

  invariant(service, "Service not found");

  // Create state for each editable field
  const [name, setName] = useState(service.name);
  const [description, setDescription] = useState(service.description || "");
  const [descriptionShort, setDescriptionShort] = useState(
    service.descriptionShort || ""
  );
  const [isTemplate, setIsTemplate] = useState(service.isTemplate);
  const [requestPermissionState, setRequestPermissionState] =
    useState<ShareableState>(service.requestPermissionState);
  const [requestPermissionList, setRequestPermissionList] = useState<string[]>(
    service.requestPermissionList
  );
  const [tags, setTags] = useState<string[]>(service.tags);
  const [scheduleType, setScheduleType] = useState<ScheduleType | null>(
    service.scheduleType
  );

  const [hasChanges, setHasChanges] = useState(false);

  // Check for changes in any field
  useEffect(() => {
    const isChanged =
      name !== service.name ||
      description !== (service.description || "") ||
      descriptionShort !== (service.descriptionShort || "") ||
      isTemplate !== (service.isTemplate || false) ||
      requestPermissionState !== service.requestPermissionState ||
      JSON.stringify(requestPermissionList) !==
        JSON.stringify(service.requestPermissionList) ||
      JSON.stringify(tags) !==
        JSON.stringify(
          Array.isArray(service.tags)
            ? service.tags
            : JSON.parse((service.tags as string) || "[]")
        ) ||
      scheduleType !== service.scheduleType;

    setHasChanges(isChanged);
  }, [
    name,
    description,
    descriptionShort,
    isTemplate,
    requestPermissionState,
    requestPermissionList,
    tags,
    scheduleType,
    service,
  ]);

  const handleSave = () => {
    editServiceMutation.mutate({
      id: serviceId,
      data: {
        name,
        description,
        descriptionShort,
        isTemplate,
        requestPermissionState,
        requestPermissionList,
        tags,
        scheduleType,
      },
    });
  };

  const isCodeWordsLibraryOwned = service.ownerId === CODEWORDS_LIBRARY_USER_ID;

  return (
    <div className="relative space-y-4">
      <div className="flex justify-between">
        <Badge
          className="rounded-md text-xs"
          variant={isCodeWordsLibraryOwned ? "green" : "danger"}
        >
          {isCodeWordsLibraryOwned
            ? "CodeWords Library Owned"
            : "Non-CodeWords Library Owned"}
        </Badge>
        <Button
          size="sm"
          className="rounded-md text-xs"
          disabled={!hasChanges || editServiceMutation.isPending}
          onClick={handleSave}
        >
          {editServiceMutation.isPending ? "Saving..." : "Save"}
        </Button>
      </div>

      <div className="space-y-4 pt-2">
        <div className="space-y-2">
          <label className="text-sm font-medium">Service Name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Service Name"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Is Template</label>
          <Checkbox
            className="block"
            checked={isTemplate}
            onCheckedChange={(checked) => setIsTemplate(checked === true)}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium">Description </label>
            <TooltipWrapper
              content="Markdown is supported."
              className="max-w-80"
            >
              <InfoIcon size={14} className="text-gray-500" />
            </TooltipWrapper>
          </div>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description"
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium">Short Description </label>
            <TooltipWrapper
              className="max-w-80"
              content="Markdown is not supported. This is used to display a concise description for the template gallery page."
            >
              <InfoIcon size={14} className="text-gray-500" />
            </TooltipWrapper>
          </div>
          <Textarea
            value={descriptionShort}
            onChange={(e) => setDescriptionShort(e.target.value)}
            placeholder="Short Description"
            rows={2}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Visibility</label>
          <Select
            value={requestPermissionState}
            onValueChange={(value) =>
              setRequestPermissionState(value as ShareableState)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select visibility" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PRIVATE">Private</SelectItem>
              <SelectItem value="USER_ALLOWLIST">User Allowlist</SelectItem>
              <SelectItem value="PUBLIC">Public</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {requestPermissionState === "USER_ALLOWLIST" && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Allowed Emails</label>
            <InputTags
              value={requestPermissionList}
              onChange={setRequestPermissionList}
              placeholder="Add emails (press Enter or comma to add)"
              validator={(email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
            />
            <p className="text-xs text-gray-500">
              Enter email addresses to allow access
            </p>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium">Tags</label>
            <TooltipWrapper
              className="max-w-80"
              content="Tags need to be written with case and space-sensivity. E.g. 'SEO' instead of 'seo', 'Web Scraping' instead of 'web-scraping'."
            >
              <InfoIcon size={14} className="text-gray-500" />
            </TooltipWrapper>
          </div>
          <InputTags value={tags} onChange={setTags} placeholder="Add tags" />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Schedule Type</label>
          <Select
            value={scheduleType || ""}
            onValueChange={(value) => {
              if (value === "None") {
                setScheduleType(null);
              } else {
                setScheduleType(value as ScheduleType);
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select schedule type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={"None"}>None</SelectItem>
              <SelectItem value="ScheduleFriendly">
                Schedule Friendly
              </SelectItem>
              <SelectItem value="ScheduleOnly">Schedule Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Created At</label>
          <Input
            value={new Date(service.createdAt).toLocaleString()}
            disabled
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Updated At</label>
          <Input
            value={new Date(service.updatedAt).toLocaleString()}
            disabled
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Service ID</label>
          <Input value={service.id} disabled />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Owner ID</label>
          <Input value={service.ownerId} disabled />
        </div>
      </div>
    </div>
  );
};
